// The money a school-fee payer is charged, read once and compared as integers.
//
// The fee arrives as an exact fixed-2 decimal string ("12500.00") and the pay
// contract carries a float64 number, so something has to cross between them.
// The crossing is done ONCE, at the wire, and every decision before it -- is
// this a well-formed amount, is it within the fee, has the payer typed a third
// decimal -- is taken on integer minor units. Comparing a charge with a float
// is how a ceiling becomes approximate, and the ceiling is the only thing
// standing between a partial payment and an overpayment.

// The server compares in minor units and refuses a third decimal place.
export const MAX_AMOUNT_DECIMALS = 2;

const AMOUNT_PATTERN = /^\d+(\.\d{1,2})?$/;

// Keystroke filter for the school-fee amount field. Unlike the deposit rail's
// whole-unit `digitsOnly`, a fee may legitimately carry minor units, so a
// single separator is allowed -- and no more than two digits after it, because
// a third is refused by the server rather than rounded by it.
export const amountKeystrokes = (value: string): string => {
  const cleaned = value.replace(/[^0-9.]/g, "");
  const [whole, ...rest] = cleaned.split(".");

  if (rest.length === 0) return whole;

  return `${whole}.${rest.join("").slice(0, MAX_AMOUNT_DECIMALS)}`;
};

// Exact minor units, or null when the string is not a well-formed amount.
// Null rather than NaN: every caller has to decide what to do with "not an
// amount", and NaN silently compares false to everything instead.
export const toMinorUnits = (amount: string): number | null => {
  const trimmed = amount.trim();

  if (!AMOUNT_PATTERN.test(trimmed)) return null;

  const [whole, fraction = ""] = trimmed.split(".");

  return (
    Number(whole) * 100 + Number(fraction.padEnd(MAX_AMOUNT_DECIMALS, "0"))
  );
};


export type ServiceAmountBounds = {
  min?: number;
  max?: number;
};

// Positive, well formed, no more than the fee, and -- when the corporate
// service states either -- no less than its own minimum and no more than its
// own maximum. A malformed fee refuses the payment rather than waving it
// through: an unreadable ceiling is not the same as no ceiling. A fee that
// must be paid in full accepts only the fee itself.
export const isWithinFee = (
  amount: string,
  fee: string,
  serviceBounds: ServiceAmountBounds = {},
  { partialAllowed = true }: { partialAllowed?: boolean } = {}
): boolean => {
  const entered = toMinorUnits(amount);
  const ceiling = toMinorUnits(fee);

  if (entered === null || ceiling === null) return false;
  if (entered <= 0 || entered > ceiling) return false;
  if (!partialAllowed && entered !== ceiling) return false;

  const { min, max } = serviceBounds;

  if (typeof min === "number" && min > 0 && entered < Math.round(min * 100)) {
    return false;
  }

  if (typeof max === "number" && max > 0 && entered > Math.round(max * 100)) {
    return false;
  }

  return true;
};

// The value the pay body carries. The contract types `amount` as a float64
// number (D-HARM-16), so the conversion happens here and nowhere else -- and
// only after `isWithinFee` has already decided, on integers, that the payer
// may pay it.
export const wireAmount = (amount: string): number => Number(amount.trim());
